import React, { useState, useContext } from 'react'
import { useHistory } from 'react-router'
import { toast } from 'react-toastify'
import { useExperiment } from '@deal/experiment-js'
import logger from '#src/services/logger'
import MessageSentToast from '#src/components/MessageSentToast'
import { IdentityContext } from '#src/containers/Identity'
import { ConversationStarterFragment } from '../../fragments/ConversationStarter.generated'
import { useStartExpertConsumerConversationMutation } from '#src/mutations/StartExpertConsumerConversation.generated'
import { useRecommendedExpertForConversationStartersLazyQuery } from '../../components/ExpertConversationStarters/RecommendedExpertForConversationStarters.generated'
import { StartExpertConsumerConversationSourcePageInput } from '../../generated/types'
import { generateCreateUrlFromCategory } from '#src/services/path'

type CreateConversationInput = {
  userId?: string
  conversationStarterToSend: ConversationStarterFragment
  sourcePage: StartExpertConsumerConversationSourcePageInput
  categoryId: string
  categorySlug: string
}

type ExpertConversationStartersContext = {
  createConversation: (input: CreateConversationInput) => void
  messageIndexSent?: number | undefined
  messageIndexToSend?: number | undefined
  setMessageIndexToSend: (index: number) => void
}

const ExpertConversationStartersContext = React.createContext<ExpertConversationStartersContext>({
  createConversation: () => {},
  setMessageIndexToSend: () => {}
})

const { Consumer, Provider } = ExpertConversationStartersContext

const userFacingErrorMessage =
  'Shoot, we tried messaging an expert but something went wrong. Try sending a message yourself!'

const ExpertConversationStartersContextProvider: React.FC = ({ children }) => {
  const { myself } = useContext(IdentityContext)
  const history = useHistory()
  const [showConfirmationToast, setShowConfirmationToast] = useState(false)
  const [messageIndexToSend, setMessageIndexToSend] = useState<number>()
  const [messageIndexSent, setMessageIndexSent] = useState<number>()
  const [userId, setUserId] = useState<string>()
  const [categorySlug, setCategorySlug] = useState<string>()
  const [conversationStarterToSend, setConversationStarterToSend] = useState<
    ConversationStarterFragment
  >()
  const [sourcePage, setSourcePage] = useState<StartExpertConsumerConversationSourcePageInput>()

  const { result: conversationStartersPathResult } = useExperiment({
    experiment: 'conversation-starters-path',
    defaultTreatment: 'control',
    options: {
      skip: !!myself
    }
  })

  const [startExpertConsumerConversation] = useStartExpertConsumerConversationMutation({
    refetchQueries: ['GetConversationByConsumerAndCategoryId'],
    onCompleted: data => {
      if (!data || !data.startExpertConsumerConversation.conversation?.id) {
        logger.captureException(
          new Error('No conversation returned from startExpertConsumerConversation')
        )
        toast.error(userFacingErrorMessage)
        return
      }

      setMessageIndexSent(messageIndexToSend)
      setShowConfirmationToast(true)
    },
    onError: error => {
      logger.captureException(error)
      toast.error(userFacingErrorMessage)
    }
  })

  const [fetchRecommendedExpert] = useRecommendedExpertForConversationStartersLazyQuery({
    onCompleted: data => {
      const refetchedExpert = data.category.recommendExpertForConsumer.expert

      /**
       * If the user sends a convo starters and gets a placeholder expert from the refetch it leads to an awkward state
       * where the old expert shows up in the sidebar, but no message is sent because the authenticatedChatUserId
       * will not update.
       */

      if (
        !refetchedExpert ||
        refetchedExpert.expertAttributes.placeholderExpert ||
        !conversationStarterToSend
      ) {
        toast.error(userFacingErrorMessage)
        return
      }

      if (myself || conversationStartersPathResult === 'control') {
        startExpertConsumerConversation({
          variables: {
            input: {
              consumerId: userId,
              expertId: refetchedExpert.id,
              sourceFlow: {
                conversationStarterFlow: {
                  conversationStarterDefinitionId: conversationStarterToSend.definition.id,
                  title: conversationStarterToSend.title,
                  message: conversationStarterToSend.message
                }
              },
              sourcePage
            }
          }
        })
      } else {
        const pathUrl = generateCreateUrlFromCategory({
          maybeCategorySlug: categorySlug,
          sourceKey: 'conversation-starters',
          pathIntent: 'request',
          prefillData: {
            expertId: refetchedExpert.id,
            conversationStarterDefinitionId: conversationStarterToSend.definition.id,
            conversationStarterSellableId: sourcePage?.sellablePage?.sellableId
          }
        })
        history.push(pathUrl)
      }
    }
  })

  // Create a conversation and with the recommended expert.
  const createConversation = (input: CreateConversationInput) => {
    setCategorySlug(input.categorySlug)
    setUserId(input.userId)
    setConversationStarterToSend(input.conversationStarterToSend)
    setSourcePage(input.sourcePage)
    fetchRecommendedExpert({
      variables: {
        categoryId: input.categoryId
      }
    })
  }

  return (
    <>
      {showConfirmationToast && (
        <MessageSentToast
          headerText="Question sent to your expert"
          subheaderText="Your expert is currently online. It may take a moment for your question to appear in the chat."
          duration={3500}
          onTimeout={() => setShowConfirmationToast(false)}
        />
      )}
      <Provider
        value={{
          createConversation,
          messageIndexSent,
          messageIndexToSend,
          setMessageIndexToSend: (index: number) => setMessageIndexToSend(index)
        }}
      >
        {children}
      </Provider>
    </>
  )
}

const useExpertConversationStartersContext = () => {
  const conversationStarterContext = useContext(ExpertConversationStartersContext)

  if (!ExpertConversationStartersContext) {
    throw new Error('Invoked ExpertConversationStartersContext outside of provider')
  }

  return conversationStarterContext
}

export {
  ExpertConversationStartersContextProvider,
  ExpertConversationStartersContext,
  Consumer as ExpertConversationStartersContextConsumer,
  useExpertConversationStartersContext
}
